import React, { useState, useEffect, lazy, Suspense, memo } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { 
  Home, 
  CheckSquare, 
  Users, 
  FileText, 
  User, 
  Cog, 
  ShoppingBag, 
  DollarSign,
  LogOut, 
  Shield,
  Menu,
  X,
  Mail,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { onAuthStateChange, logout } from './services/firebase';

// Lazy load ALL components for better performance
const Dashboard = lazy(() => import('./components/Dashboard'));
const CheckIn = lazy(() => import('./components/CheckIn'));
const MemberCheckIn = lazy(() => import('./components/MemberCheckIn'));
const Invoices = lazy(() => import('./components/Invoices'));
const Customers = lazy(() => import('./components/Customers'));
const Settings = lazy(() => import('./components/Settings'));
const Shop = lazy(() => import('./components/Shop'));
const Financials = lazy(() => import('./components/Financials'));
const Login = lazy(() => import('./components/Login'));
const PublicInvoiceViewer = lazy(() => import('./components/PublicInvoiceViewer'));
const MassEmail = lazy(() => import('./components/MassEmail'));

// Optimized Loading Component
const LoadingSpinner = memo(() => (
  <div className="min-h-screen bg-slate-900 flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
  </div>
));

// Memoized Navigation Item
const NavigationItem = memo(({ item, activeTab, onClick, isCollapsed }) => {
  const { id, icon: Icon, label, color } = item;
  const isActive = activeTab === id;

  return (
    <li>
      <button
        onClick={() => onClick(id)}
        className={`relative w-full group transition-all duration-150 ${
          isActive ? 'scale-[1.01]' : 'hover:scale-[1.005]'
        }`}
      >
        <div className={`relative flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all duration-150 ${
          isActive
            ? 'bg-white/10 text-white border border-white/10'
            : 'bg-white/5 text-slate-300 hover:bg-white/8 hover:text-white'
        }`}>
          
          <div className={`p-2 rounded-lg transition-all duration-150 ${
            isActive 
              ? `bg-gradient-to-br ${color}` 
              : 'bg-white/5'
          }`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          
          {!isCollapsed && (
            <div className="flex-1 text-left">
              <div className="font-medium text-sm">{label}</div>
            </div>
          )}
        </div>
      </button>
    </li>
  );
});

function MainApp() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [navigationParams, setNavigationParams] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isPublicRoute = location.pathname.startsWith('/invoice/');

  useEffect(() => {
    const unsubscribe = onAuthStateChange((user) => {
      setIsAuthenticated(!!user);
      setUser(user);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Memoized handlers
  const handleNavigation = React.useCallback((tab, params = null) => {
    setActiveTab(tab);
    setNavigationParams(params);
    setMobileMenuOpen(false);
  }, []);

  const handleRegularNavigation = React.useCallback((tab) => {
    setActiveTab(tab);
    setNavigationParams(null);
    setMobileMenuOpen(false);
  }, []);

  const handleLogout = React.useCallback(async () => {
    await logout();
    setIsAuthenticated(false);
    setUser(null);
    setActiveTab('dashboard');
    setNavigationParams(null);
  }, []);

  // Simplified navigation items
  const navigationItems = React.useMemo(() => [
    { id: 'dashboard', icon: Home, label: 'Dashboard', color: 'from-blue-500 to-cyan-600' },
    { id: 'checkin', icon: CheckSquare, label: 'Guest Check-in', color: 'from-emerald-500 to-teal-600' },
    { id: 'members', icon: Users, label: 'Member Check-in', color: 'from-purple-500 to-violet-600' },
    { id: 'invoices', icon: FileText, label: 'Invoices', color: 'from-indigo-500 to-purple-600' },
    { id: 'financials', icon: DollarSign, label: 'Financials', color: 'from-emerald-500 to-green-600' },
    { id: 'customers', icon: User, label: 'Customers', color: 'from-cyan-500 to-blue-600' },
    { id: 'shop', icon: ShoppingBag, label: 'Shop', color: 'from-green-500 to-emerald-600' },
    { id: 'massemail', icon: Mail, label: 'Mass Email', color: 'from-purple-500 to-pink-600' },
    { id: 'settings', icon: Cog, label: 'Settings', color: 'from-slate-500 to-gray-600' },
  ], []);

  // FIXED: Optimized tab rendering without key spread
  const renderTab = React.useCallback(() => {
    switch (activeTab) {
      case 'dashboard': 
        return <Dashboard onNavigate={handleNavigation} />;
      case 'checkin':   
        return <CheckIn />;
      case 'members':   
        return <MemberCheckIn />;
      case 'invoices':  
        return <Invoices invoiceToOpen={navigationParams} />;
      case 'financials':  
        return <Financials />;
      case 'customers': 
        return <Customers />;
      case 'shop':      
        return <Shop />;
      case 'massemail': 
        return <MassEmail />;
      case 'settings':  
        return <Settings />;
      default:          
        return <Dashboard onNavigate={handleNavigation} />;
    }
  }, [activeTab, navigationParams, handleNavigation]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (isPublicRoute) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/invoice/:invoiceId" element={<PublicInvoiceViewer />} />
        </Routes>
      </Suspense>
    );
  }

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <Login onLogin={React.useCallback((success, firebaseUser = null) => {
          if (success && firebaseUser) {
            setIsAuthenticated(true);
            setUser(firebaseUser);
          }
        }, [])} />
      </Suspense>
    );
  }

  return (
    <div className="h-screen flex bg-slate-900 text-white relative">
      
      {/* Simplified Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-purple-900 pointer-events-none"></div>

      {/* Optimized Sidebar */}
      <aside className={`${
        sidebarCollapsed ? 'w-16' : 'w-72'
      } ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      } fixed lg:relative z-50 lg:z-auto h-full transition-all duration-200 bg-white/5 flex flex-col border-r border-white/10`}>
        
        {/* Logo Section */}
        <div className="relative p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              
              {!sidebarCollapsed && (
                <div className="flex-1">
                  <h1 className="text-xl font-bold text-cyan-400">BREAKWATER</h1>
                  <p className="text-xs text-slate-400">Management</p>
                </div>
              )}
            </div>

            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden lg:flex p-1.5 bg-white/5 hover:bg-white/10 rounded-lg transition-all duration-200"
            >
              {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>

            <button
              onClick={() => setMobileMenuOpen(false)}
              className="lg:hidden p-1.5 bg-white/5 hover:bg-white/10 rounded-lg transition-all duration-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 p-3 overflow-y-auto custom-scrollbar">
          <ul className="space-y-1">
            {navigationItems.map((item) => (
              <NavigationItem
                key={item.id}
                item={item}
                activeTab={activeTab}
                onClick={handleRegularNavigation}
                isCollapsed={sidebarCollapsed}
              />
            ))}
          </ul>
        </nav>

        {/* User Section */}
        <div className="p-3 border-t border-white/10">
          {user && !sidebarCollapsed && (
            <div className="p-3 bg-white/5 rounded-lg mb-3">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white text-xs font-semibold">
                    {user.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-white truncate">Admin</p>
                  <p className="text-xs text-slate-400 truncate">{user.email}</p>
                </div>
              </div>
            </div>
          )}
          
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center space-x-2 px-3 py-2 rounded-lg text-slate-300 hover:bg-red-500/10 hover:text-white transition-all duration-200"
          >
            <LogOut className="w-4 h-4" />
            {!sidebarCollapsed && <span className="text-sm">Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-30 p-2 bg-white/10 rounded-lg text-white hover:bg-white/20 transition-all duration-200"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/30 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto relative">
        <div className="relative z-10">
          <Suspense fallback={<LoadingSpinner />}>
            {renderTab()}
          </Suspense>
        </div>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route path="/invoice/:invoiceId" element={<PublicInvoiceViewer />} />
        <Route path="/*" element={<MainApp />} />
      </Routes>
    </Suspense>
  );
}